import { useEffect, useState } from "react";
import { Check, Copy, Gift, Loader2, Mail, Send, Share2, Users, Wallet2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export default function ReferralTab() {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrals/mine").then(({ data }) => setData(data.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const eur = (v) => {
    try { return new Intl.NumberFormat(lang, { style: "currency", currency: "EUR" }).format(v || 0); }
    catch { return `€${(v || 0).toFixed(2)}`; }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>;
  if (data.error) return <p data-testid="referral-error" className="text-sm text-slate-400 text-center py-10">{t("misc.error")}</p>;

  const shareMsg = t("ref.shareMsg").replace("{link}", data.link);
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard negato */ }
  };
  const shareNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: "SIMAMMA", text: shareMsg }); } catch { /* annullato */ } }
  };
  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/referrals/invite", { email: email.trim() });
      toast.success(t("ref.inviteSent"));
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const stats = [
    ["registered", Users, data.stats.registered, t("ref.statRegistered")],
    ["purchased", Gift, data.stats.purchased, t("ref.statPurchased")],
    ["earned", Share2, eur(data.stats.earned), t("ref.statEarned")],
    ["wallet", Wallet2, eur(data.stats.wallet_credited), t("ref.statWallet")],
    ["payable", Wallet2, eur(data.stats.payable), t("ref.statPayable")],
    ["paid", Check, eur(data.stats.paid_out), t("ref.statPaid")],
  ];

  const kindLabel = { commission: t("ref.typeCommission"), clawback: t("ref.typeClawback"), payout: t("ref.typePayout"), bonus: t("ref.typeBonus") };

  return (
    <div data-testid="referral-tab" className="space-y-6">
      <div className="rounded-3xl bg-[#1E293B] text-white p-7 sm:p-9">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl">{t("ref.title").replace("{pct}", data.pct)}</h2>
        <p className="mt-2 text-sm text-slate-300 max-w-xl">{t("ref.sub").replace("{pct}", data.pct).replace("{friend}", data.friend_pct)}</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <div data-testid="referral-link" className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-3 font-mono-t text-sm truncate select-all">{data.link}</div>
          <div className="flex gap-2">
            <button data-testid="referral-copy" onClick={copy}
              className="rounded-full bg-[#FF6B35] px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors flex items-center gap-1.5">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? t("ref.copied") : t("ref.copy")}
            </button>
            <a data-testid="referral-share-wa" href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`} target="_blank" rel="noreferrer"
              className="rounded-full bg-white/10 border border-white/15 px-4 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-white/20 transition-colors">WhatsApp</a>
            <a data-testid="referral-share-mail" href={`mailto:?subject=${encodeURIComponent("SIMAMMA")}&body=${encodeURIComponent(shareMsg)}`}
              className="rounded-full bg-white/10 border border-white/15 px-4 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-white/20 transition-colors flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
            {typeof navigator !== "undefined" && navigator.share && (
              <button data-testid="referral-share-native" onClick={shareNative}
                className="rounded-full bg-white/10 border border-white/15 px-4 py-3 hover:bg-white/20 transition-colors">
                <Share2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="referral-stats">
        {stats.map(([id, Icon, val, label]) => (
          <div key={id} data-testid={`referral-stat-${id}`} className="rounded-2xl bg-white border border-slate-200/80 p-4">
            <Icon className="w-4 h-4 text-[#FF6B35]" />
            <div className="mt-2 font-display font-extrabold text-xl text-slate-900">{val}</div>
            <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-400 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <form onSubmit={invite} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6" data-testid="referral-invite-form">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-1">{t("ref.inviteTitle")}</h3>
        <div className="flex gap-2 mt-3">
          <input data-testid="referral-invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={t("ref.invitePh")}
            className="flex-1 rounded-full border border-slate-200 px-5 py-3 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
          <button data-testid="referral-invite-send" type="submit" disabled={busy}
            className="rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {t("ref.inviteSend")}
          </button>
        </div>
      </form>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto" data-testid="referral-entries">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("ref.entries")}</h3>
        {data.entries.length === 0 ? (
          <p data-testid="referral-empty" className="py-8 text-center text-sm text-slate-400">{t("ref.empty")}</p>
        ) : (
          <table className="w-full min-w-[560px] text-sm">
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.entry_id} data-testid={`referral-entry-${e.entry_id}`} className="border-b border-slate-50 last:border-0">
                  <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-500 whitespace-nowrap">
                    {new Date(e.created_at).toLocaleDateString(lang, { day: "2-digit", month: "2-digit", year: "2-digit" })}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${e.kind === "commission" ? "bg-emerald-50 text-emerald-700" : e.kind === "clawback" ? "bg-red-50 text-red-600" : e.kind === "bonus" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                      {kindLabel[e.kind] || e.kind}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-500">{e.referee_email || "—"}</td>
                  <td className={`py-2.5 text-right font-mono-t text-xs font-bold ${e.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {e.amount >= 0 ? "+" : ""}{eur(e.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-4 text-xs text-slate-400">{t("ref.note")}</p>
      </div>
    </div>
  );
}
